/** @jsx jsx */
import { Icon, IconButton, Spacer, Spinner } from "@cd2/components/build/es6"
import { FileUploader, IFileData, Image } from "@cd2/fileboy-react"
import { Form2 } from "@cd2/forms/build/es6"
import { P } from "@cd2/text/build/es6"
import { useTheme } from "@cd2/theme/build/es6"
import { css, jsx } from "@emotion/core"
import { icons } from "../../constants/icons"
import { colors } from "../EOSThemeProvider"

export function ImageUploadField({
  onChange,
  value,
  isClearable,
  label,
  required,
  preFieldText,
  postFieldText,
  maxPreviewHeight,
  maxPreviewWidth,
  error,
  disabled,
}: {
  onChange: (file: IFileData | null) => void
  value?: string | null
  isClearable?: boolean
  label?: React.ReactNode
  required?: boolean
  preFieldText?: React.ReactNode
  postFieldText?: React.ReactNode
  maxPreviewHeight?: number
  maxPreviewWidth?: number
  error?: string
  disabled?: boolean
}) {
  const { themeColor } = useTheme()
  return (
    <div
      css={
        disabled
          ? css`
              opacity: 0.6;
              pointer-events: none;
            `
          : undefined
      }
    >
      {label ? (
        <Form2.FieldLabel fieldName="image" label={label} required={required} errored={!!error} />
      ) : null}
      {preFieldText && <Spacer size={4} />}
      {preFieldText}
      {label && <Spacer size={4} />}
      <FileUploader
        key={value ?? ""}
        onFileUploaded={onChange}
        defaultFileId={value ?? ""}
        render={({ DropZone, isUploading, openFileDialogue, onRemoveFile }) =>
          value ? (
            <div
              css={css`
                background-color: #f4f4f4;
                border-radius: 3px;
                overflow: hidden;
                display: flex;
                justify-content: center;
                align-items: center;
                cursor: ${disabled ? "default" : "pointer"};
                position: relative;
                min-height: 80px;
              `}
            >
              {isClearable && !disabled && (
                <div
                  css={css`
                    position: absolute;
                    top: 0;
                    right: 0;
                  `}
                >
                  <IconButton
                    icon={icons.delete}
                    buttonColor="red"
                    onClick={() => {
                      onRemoveFile?.()
                      onChange(null)
                    }}
                    square
                  />
                </div>
              )}
              <Image
                id={value}
                css={css`
                  ${maxPreviewHeight && `max-height: ${maxPreviewHeight}px !important;`}
                  ${maxPreviewWidth && `max-width: ${maxPreviewWidth}px !important;`}
                `}
              />
            </div>
          ) : (
            <DropZone>
              <div
                css={css`
                  border: 1px dashed ${error ? themeColor("red") : colors.lightBorder};
                  border-radius: 6px;
                  padding: 16px;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  cursor: ${disabled ? "default" : "pointer"};
                  background-color: #f4f4f4;
                  min-height: 80px;
                `}
                onClick={disabled ? undefined : openFileDialogue}
              >
                {isUploading ? (
                  <Spinner color="black" />
                ) : (
                  <div>
                    <P size="large" align="center">
                      <Icon icon={icons.upload} /> Upload an image
                    </P>
                    <Spacer size={4} />
                    <P align="center" size="small">
                      drag and drop images to upload
                    </P>
                  </div>
                )}
              </div>
            </DropZone>
          )
        }
        allow="image/*"
      />
      {error && (
        <P size="small" color="red">
          {error}
        </P>
      )}
      {postFieldText && <Spacer size={4} />}
      {postFieldText}
    </div>
  )
}
